import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { plainToInstance } from 'class-transformer';
import { Observable, combineLatest, filter, map, switchMap, takeUntil } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { User } from '../../../auth/models/user.model';
import { LoginService } from '../../../auth/services/login.service';
import { Company } from '../../../models/company.model';
import { CompanyService } from '../../../services/company.service';
import { ComponentCommService } from '../../../services/component-comm.service';
import { PusherService } from '../../../services/pusher.service';
import { checkAccept } from '../../../utilities/file';
import { SubscriptionManager } from '../../../utilities/subscription-manager';
import { ChatMessage } from '../../models/chat-message.model';
import { ChatRoom } from '../../models/chat-room.model';
import { ChatService } from '../../services/chat.service';

@Component({
  selector: 'ag-center',
  templateUrl: './center.component.html',
  styleUrls: ['./center.component.scss']
})
export class CenterComponent extends SubscriptionManager implements OnInit, OnDestroy {

  @ViewChild('dropArea', { static: true }) private readonly dropArea: ElementRef;
  @ViewChild('dropLegend') private readonly dropLegend: ElementRef;
  @ViewChild('scrollMe') private readonly scrollMe: ElementRef;

  /**
   * Specify what file types the user can attach to a chat.
   *
   * Works as
   * [[https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept|HTML attribute: accept]].
   */
  public accept: string = "";
  public chatHeight: number = 0;
  public enableAttachments: boolean = true;
  public logosOverlap: number = 20; // in pixels
  public newMessage: { [roomId: string]: string } = {}; // Input ngModel
  /**
   * Flag used to enable/disable UI buttons and links when an API request is in
   * progress.
   */
  public processing: boolean;
  public selectedIndex: number;
  public unread: { [roomId: string]: boolean };

  // Observables for async pipe
  public user$: Observable<User>;
  public company$: Observable<Company>;
  public rooms$: Observable<ChatRoom[]>;
  public viewData$: Observable<{ user: User; company: Company; rooms: ChatRoom[] }>;

  // Cached values for methods that need them
  private currentUser: User;
  private currentCompany: Company;
  private currentRooms: ChatRoom[];

  constructor(
    private chatService: ChatService,
    private componentComm: ComponentCommService,
    private pusherService: PusherService,
    private loginService: LoginService,
    private companyService: CompanyService,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    if (environment.modules.chat) {
      this.componentComm.emit({ name: 'app-title', title: 'MESSENGER_CENTER.TITLE' });

      // Setup observables for async pipe
      this.user$ = this.loginService.getCurrentUser().pipe(takeUntil(this.destroy$));

      this.company$ = this.user$.pipe(
        filter(user => !!user?.accounts),
        switchMap(() => this.companyService.watch()),
        filter(company => !!company),
        takeUntil(this.destroy$)
      );

      this.rooms$ = this.company$.pipe(
        switchMap(company => this.chatService.get(company.id)),
        map(response => response.body || []),
        takeUntil(this.destroy$)
      );

      // Combine all observables for template
      this.viewData$ = combineLatest([this.user$, this.company$, this.rooms$]).pipe(
        map(([user, company, rooms]) => {
          // Cache values for methods
          this.currentUser = user;
          this.currentCompany = company;
          this.currentRooms = rooms;
          this.checkUnread();
          return { user, company, rooms };
        }),
        takeUntil(this.destroy$)
      );

      // Setup Pusher subscription
      this.company$.pipe(
        switchMap(company =>
          this.pusherService.listen(`company_${company.id}`, 'chat').pipe(
            map(event => ({ event, company }))
          )
        ),
        takeUntil(this.destroy$)
      ).subscribe(({ event }) => {
        if (event.data && event.data.room_id) {
          const roomIndex = this.currentRooms.findIndex(room => room.id === event.data.room_id);

          if (roomIndex !== -1) {
            if (event.data.message) {
              this.currentRooms[roomIndex].messages = this.currentRooms[roomIndex].messages.filter(message => !message.sending);
              this.currentRooms[roomIndex].messages.push(plainToInstance(ChatMessage, event.data.message));

              if (roomIndex === this.selectedIndex) this.updateChatHeight();
            }
            if (event.data.context) {
              this.currentRooms[roomIndex].context = event.data.context;
            }

            this.checkUnread();
          } else {
            this.loadRooms();
          }
        }
      });

      // Setup drag and drop after company is loaded
      this.company$.subscribe(() => {
        this.setupDrop(this.dropArea);
      });

    } else this.router.navigateByUrl('/companies');
  }

  private loadRooms(): void {
    this.chatService.get(this.currentCompany.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.currentRooms = response.body || [];
        this.checkUnread();
      });
  }

  public setRoom(roomIndex: number): void {
    if (!this.processing) {
      if (this.selectedIndex !== roomIndex) {
        const room = this.currentRooms[roomIndex];

        if (!this.newMessage[room.id]) this.newMessage[room.id] = "";
        this.selectedIndex = roomIndex;

        if (room.messages?.length === 1) this.getRoom(roomIndex);
        else {
          this.updateChatHeight();
          this.setReaded();
        }
      } else {
        this.selectedIndex = undefined;
      }
    }
  }

  public submit(fileList?: FileList): void {
    this.processing = true;
    const roomId = this.currentRooms[this.selectedIndex].id;
    const fileArray = fileList ? Array.from(fileList) : undefined;

    this.send(roomId, fileArray);
  }

  private send(roomId: string, fileArray?: File[]): void {
    const phantomMessage: ChatMessage = {
      user: this.currentUser,
      company: this.currentCompany,
      created_at: new Date(),
      sending: true
    };

    if (fileArray?.length > 0) {
      // Primero, debo enviar archivos
      const file = fileArray.shift();

      this.currentRooms[this.selectedIndex].messages.push({
        file: {
          name: file.name,
          type: file.type,
          size: file.size
        }, ...phantomMessage
      });

      this.chatService.uploadFile(this.currentCompany.id, roomId, file)
        .pipe(takeUntil(this.destroy$))
        .subscribe(message => {
          this.send(roomId, fileArray);
        });
    } else if (this.newMessage[roomId] && this.newMessage[roomId].trim() !== "") {
      this.currentRooms[this.selectedIndex].messages.push({ text: this.newMessage[roomId], ...phantomMessage });

      this.chatService.send(this.currentCompany.id, roomId, this.newMessage[roomId])
        .pipe(takeUntil(this.destroy$))
        .subscribe(message => {
          this.newMessage[roomId] = undefined;
          this.processing = false;
        });
    } else {
      this.processing = false;
    }
    this.updateChatHeight();
  }

  private getRoom(roomIndex: number): void {
    this.processing = true;
    this.chatService.getRoom(this.currentCompany.id, this.currentRooms[roomIndex].id).subscribe(room => {
      this.currentRooms[roomIndex] = room; // actualizo
      this.processing = false;
      this.focus();
      this.updateChatHeight();
    });
  }

  private setReaded(): void {
    const room = this.currentRooms[this.selectedIndex];

    if (room.setReaded(this.currentUser)) {
      this.chatService.context(this.currentCompany.id, room).subscribe(context => {
        this.currentRooms[this.selectedIndex].context = context;
      });
    }
  }

  private focus(delay: number = 0): void {
    setTimeout(() => {
      const input = document.getElementById('inputElement');
      if (input) input.focus();
    }, delay);
  }

  private updateChatHeight(): void {
    this.setReaded();
    setTimeout(() => {
      // Scroll pane down to the last message
      if (this.scrollMe) this.chatHeight = this.scrollMe.nativeElement.scrollHeight;
    });
  }

  private checkUnread(): void {
    this.unread = {};

    this.currentRooms.forEach(room => {
      if (room.hasUnread(this.currentUser)) this.unread[room.id] = true;
    });
  }

  public changeFile(event): void {
    // Triggered by input component
    this.submit(event.target.files);
    event.target.value = ""; // Reset the input
  }

  private setupDrop(elSource: ElementRef): void {
    if (this.enableAttachments && elSource) {
      const el = elSource.nativeElement;

      el.addEventListener("dragenter", this.dragger, false);
      el.addEventListener("dragover", this.dragger, false);
      el.addEventListener("drop", this.drop, false);
      el.addEventListener("dragleave", this.removeDrag, false);
      el.addEventListener("dragend", this.removeDrag, false);
    }
  }

  private killDrop(elSource: ElementRef): void {
    if (elSource) {
      const el = elSource.nativeElement;

      // Removes all event listeners
      el.removeEventListener("dragenter", this.dragger);
      el.removeEventListener("dragover", this.dragger);
      el.removeEventListener("drop", this.drop);
      el.removeEventListener("dragleave", this.removeDrag);
      el.removeEventListener("dragend", this.removeDrag);
    }
  }

  private dragger = (e: Event) => {
    if (!this.processing && this.selectedIndex !== undefined) {
      e.stopPropagation();
      e.preventDefault();
      if (this.dropLegend) this.dropLegend.nativeElement.className = 'is-dragging';
    }
  };

  private drop = (e: DragEvent) => {
    if (!this.processing && this.selectedIndex !== undefined) {
      this.dragger(e);

      // Triggered by drop
      // Prevent to drag more files when the max files number is reached
      if (checkAccept(e.dataTransfer.files, this.accept)) this.submit(e.dataTransfer.files);
      // }
      this.removeDrag();
    }
  };

  private removeDrag = (e?: DragEvent) => {
    if (this.dropLegend) this.dropLegend.nativeElement.className = '';
  }

  /** @ignore */
  ngOnDestroy(): void {
    this.killDrop(this.dropArea);

    // Delete the company from contextInfo
    // if (this.contextInfo && this.company && this.account) this.chatService.setPropertiesInContext(this.contextInfo.id, this.company.id, this.account.id, false);
    // Unsubscribe from everything
    // if (this.roomsSubscriptions) this.roomsSubscriptions.unsubscribe();
    super.ngOnDestroy();
  }
}
